import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { Download, Plus, Trash2, CheckCircle2, AlertCircle, Clock, FileText, ArrowLeft, DollarSign } from "lucide-react";
import { motion } from "framer-motion";
import { PageHeader, Badge, Spinner } from "../components/Brand";
import { useApp, API } from "../AppContext";

const fmt = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(n || 0);

const STATUS_META = {
  paid: { color: "#4ADE80", icon: CheckCircle2, label_fr: "Payée", label_en: "Paid" },
  partial: { color: "#D4AF37", icon: Clock, label_fr: "Partielle", label_en: "Partial" },
  unpaid: { color: "#7BAEFA", icon: FileText, label_fr: "Impayée", label_en: "Unpaid" },
  overdue: { color: "#C00000", icon: AlertCircle, label_fr: "En retard", label_en: "Overdue" },
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const { lang } = useApp();
  const [inv, setInv] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", method: "transfert", reference: "", note: "" });
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("progere_token");

  const load = () => axios.get(`${API}/invoices/${id}`).then(({ data }) => setInv(data));
  useEffect(() => { load(); }, [id]);

  const addPayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/invoices/${id}/payments`, { ...form, amount });
      setInv(data);
      setAddOpen(false);
      setForm({ amount: "", method: "transfert", reference: "", note: "" });
    } finally { setSaving(false); }
  };

  const removePayment = async (pid) => {
    if (!window.confirm(lang === "fr" ? "Supprimer ce paiement ?" : "Delete this payment?")) return;
    const { data } = await axios.delete(`${API}/invoices/${id}/payments/${pid}`);
    setInv(data);
  };

  const downloadPdf = async () => {
    const res = await fetch(`${API}/invoices/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!inv) return <Spinner />;
  const meta = STATUS_META[inv.status] || STATUS_META.unpaid;
  const Icon = meta.icon;
  const balance = (inv.total || 0) - (inv.amount_paid || 0);
  const progress = inv.total > 0 ? Math.min(100, (inv.amount_paid / inv.total) * 100) : 0;

  const T = lang === "fr" ? {
    back: "Toutes les factures", number: "Numéro", client: "Client", project: "Projet",
    total: "Total", paid: "Payé", balance: "Solde", issue: "Émise le", due: "Échéance",
    items: "Articles", payments: "Paiements", add_payment: "Ajouter un paiement",
    amount: "Montant", method: "Méthode", reference: "Référence", note: "Note (optionnel)",
    save: "Enregistrer", cancel: "Annuler", download: "Télécharger le PDF",
    no_payments: "Aucun paiement enregistré.",
    method_transfert: "Transfert bancaire", method_cheque: "Chèque", method_comptant: "Comptant", method_autre: "Autre",
    paid_pct: (n) => `${Math.round(n)}% payé`,
  } : {
    back: "All invoices", number: "Number", client: "Client", project: "Project",
    total: "Total", paid: "Paid", balance: "Balance", issue: "Issued", due: "Due",
    items: "Items", payments: "Payments", add_payment: "Add payment",
    amount: "Amount", method: "Method", reference: "Reference", note: "Note (optional)",
    save: "Save", cancel: "Cancel", download: "Download PDF",
    no_payments: "No payments recorded.",
    method_transfert: "Bank transfer", method_cheque: "Cheque", method_comptant: "Cash", method_autre: "Other",
    paid_pct: (n) => `${Math.round(n)}% paid`,
  };

  return (
    <div data-testid="invoice-detail-page">
      <Link to="/app/invoices" className="inline-flex items-center gap-2 text-xs mb-4" style={{ color: "rgba(245,245,245,0.55)" }} data-testid="back-to-invoices">
        <ArrowLeft size={12} /> {T.back}
      </Link>
      <PageHeader title={inv.invoice_number} subtitle={`${inv.client_name}${inv.project_name ? " · " + inv.project_name : ""}`}
        action={
          <button onClick={downloadPdf} className="btn-ghost text-sm" data-testid="invoice-pdf">
            <Download size={14} /> {T.download}
          </button>
        } />

      {/* Status + progress */}
      <div className="glass-strong p-6 sm:p-8 mb-6 max-w-5xl relative overflow-hidden" data-testid="invoice-status-card">
        <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${meta.color} 0%, transparent 70%)`, transform: "translate(40%, -40%)" }} />
        <div className="relative">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}55` }}>
                <Icon size={20} color={meta.color} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest" style={{ color: "rgba(245,245,245,0.5)" }}>
                  {lang === "fr" ? "Statut" : "Status"}
                </div>
                <div className="font-heading text-2xl font-bold" style={{ color: meta.color }}>
                  {lang === "fr" ? meta.label_fr : meta.label_en}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest" style={{ color: "rgba(245,245,245,0.5)" }}>{T.balance}</div>
              <div className="font-heading text-3xl font-black" style={{ color: balance > 0 ? "#F5F5F5" : "#4ADE80" }}>{fmt(balance)}</div>
            </div>
          </div>
          {/* Progress bar */}
          <div>
            <div className="flex justify-between text-xs mb-2">
              <span style={{ color: "rgba(245,245,245,0.6)" }}>{T.paid}: <strong style={{ color: "#4ADE80" }}>{fmt(inv.amount_paid)}</strong> / {fmt(inv.total)}</span>
              <span style={{ color: "#D4AF37" }}>{T.paid_pct(progress)}</span>
            </div>
            <div className="h-3 rounded-full overflow-hidden" style={{ background: "rgba(245,245,245,0.06)" }}>
              <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.6 }}
                className="h-full rounded-full"
                style={{ background: "linear-gradient(90deg, #4ADE80cc 0%, #4ADE80 100%)" }} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 max-w-5xl text-sm">
        <div className="glass-card p-4"><div className="text-xs uppercase tracking-widest mb-1" style={{ color: "rgba(245,245,245,0.5)" }}>{T.total}</div><div className="font-heading text-lg font-bold" style={{ color: "#F5F5F5" }}>{fmt(inv.total)}</div></div>
        <div className="glass-card p-4"><div className="text-xs uppercase tracking-widest mb-1" style={{ color: "rgba(245,245,245,0.5)" }}>{T.issue}</div><div style={{ color: "#F5F5F5" }}>{new Date(inv.issue_date).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")}</div></div>
        <div className="glass-card p-4"><div className="text-xs uppercase tracking-widest mb-1" style={{ color: "rgba(245,245,245,0.5)" }}>{T.due}</div><div style={{ color: inv.status === "overdue" ? "#FF6B6B" : "#F5F5F5" }}>{new Date(inv.due_date).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")}</div></div>
      </div>

      {/* Payments */}
      <div className="glass-card p-6 max-w-5xl" data-testid="payments-section">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-lg font-bold" style={{ color: "#D4AF37" }}>{T.payments}</h3>
          {inv.status !== "paid" && (
            <button onClick={() => setAddOpen(true)} className="btn-red text-sm" data-testid="add-payment-btn">
              <Plus size={14} /> {T.add_payment}
            </button>
          )}
        </div>

        {(inv.payments || []).length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: "rgba(245,245,245,0.5)" }}>{T.no_payments}</p>
        ) : (
          <div className="space-y-2">
            {inv.payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 rounded-xl" style={{ background: "rgba(74,222,128,0.04)", border: "1px solid rgba(74,222,128,0.15)" }} data-testid={`payment-row-${p.id}`}>
                <div className="flex items-center gap-3">
                  <DollarSign size={16} color="#4ADE80" />
                  <div>
                    <div className="font-heading text-base font-bold" style={{ color: "#F5F5F5" }}>{fmt(p.amount)}</div>
                    <div className="text-xs" style={{ color: "rgba(245,245,245,0.55)" }}>
                      {T[`method_${p.method}`] || p.method} · {new Date(p.date).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")}
                      {p.reference && ` · ${p.reference}`}
                    </div>
                  </div>
                </div>
                <button onClick={() => removePayment(p.id)} className="p-2 rounded-full hover:bg-white/5" data-testid={`payment-delete-${p.id}`}>
                  <Trash2 size={14} color="rgba(245,245,245,0.5)" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add payment modal */}
      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }} onClick={() => !saving && setAddOpen(false)} data-testid="payment-modal">
          <form onSubmit={addPayment} className="glass-strong w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-bold mb-4" style={{ color: "#D4AF37" }}>{T.add_payment}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{T.amount}</label>
                <input type="number" step="0.01" min="0.01" required className="input-dark mt-1" placeholder="0.00"
                  value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{T.method}</label>
                <select className="input-dark mt-1" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="payment-method">
                  <option value="transfert">{T.method_transfert}</option>
                  <option value="cheque">{T.method_cheque}</option>
                  <option value="comptant">{T.method_comptant}</option>
                  <option value="autre">{T.method_autre}</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{T.reference}</label>
                <input className="input-dark mt-1" placeholder="VIR-2026-001"
                  value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="payment-reference" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{T.note}</label>
                <textarea className="input-dark mt-1" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="payment-note" />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-5">
              <button type="button" className="btn-ghost text-sm" onClick={() => setAddOpen(false)} disabled={saving} data-testid="payment-cancel">{T.cancel}</button>
              <button type="submit" className="btn-red text-sm" disabled={saving} data-testid="payment-save">
                {saving ? <Spinner /> : <><CheckCircle2 size={14} /> {T.save}</>}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
